
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { Observable } from 'rxjs';
import { formatBytes, checkMIMEType } from '../../../helpers/file';
import { UploadedFile } from 'ah-api-gateways';
import { UploadedFileUpdate } from 'ah-requests';
import FileUploadStatus from './FileUploadStatus.vue';
import UploadButton from './UploadButton.vue';
import FileUploadInvalid from './FileUploadInvalid.vue';
import { BModal } from 'bootstrap-vue';
import WithRequestManager from 'ah-common-lib/src/requestManager/WithRequestManager.vue';
import { format } from 'date-fns';

/**
 * File Uploader component
 *
 * Emits:
 * - update:uploaded - .sync-able event to relay updates to the currently uploaded file. Will emit `null` after file deletion.
 * - update:selected (payload: File | null) - emitted when file is selected and ready to be uploaded or null if cancelled.
 */
@Component({
  components: {
    UploadButton,
    FileUploadStatus,
    FileUploadInvalid,
  },
})
export default class FileUploader extends Mixins(WithRequestManager) {
  $refs!: {
    deleteConfirmModal: BModal;
  };

  @Prop({ default: '' }) sectionTitle!: string;

  @Prop({ default: '' }) title!: string;

  @Prop({ default: '' }) description!: string;

  @Prop({ default: false }) hideTitle!: boolean | string;

  @Prop({ default: false }) uploadNotAllowed!: boolean | string;

  @Prop({ default: false }) deleteNotAllowed!: boolean | string;

  @Prop({ default: false }) downloadNotAllowed!: boolean | string;

  @Prop({ default: false }) hideWarning!: boolean | string;

  @Prop({ default: false }) hideFooter!: boolean | string;

  @Prop({ default: false }) hideActions!: boolean | string;

  @Prop({ default: false }) tooltip!: boolean | string;

  /**
   * Accept string for MIME types
   *
   * Should be a list of MIME types (not extensions) to allow for better JS file type vetting
   *
   */
  @Prop({ default: '' }) accept!: string;

  @Prop({ default: '' }) acceptString!: string;

  @Prop({ default: -1 }) maxSize!: number;

  /**
   * Whether to auto upload the inputed file
   *
   * Default is set to true
   */
  @Prop({ default: true }) autoUpload!: boolean | string;

  /**
   * Whether to force loading state
   *
   * Default is set to false
   */
  @Prop({ default: false }) loading!: boolean | string;

  /**
   * Currently uploaded file
   *
   * Updatable via .sync
   */
  @Prop({ default: null }) uploaded!: UploadedFile | null;

  /**
   * Currently selected file
   *
   * Updatable via .sync
   */
  @Prop({ default: null }) selected!: File | null;

  @Prop({ default: () => () => null }) uploadRequest!: (file: Blob) => Observable<UploadedFileUpdate<UploadedFile>>;

  @Prop({ default: () => () => null }) downloadRequest!: (file: UploadedFile) => Observable<void>;

  @Prop({ default: () => () => null }) deleteRequest!: (file: UploadedFile) => Observable<void>;

  @Prop({ default: 'download-button' }) downloadButtonClass!: string;

  @Prop({ default: 'btn-stroked' }) uploadButtonClass!: string;

  @Prop({ default: 'btn-danger-secondary' }) deleteButtonClass!: string;

  @Prop({ default: true }) hideUpdatedBy?: string | boolean;

  requestManagerConfig = {
    exposeToParent: true,
    onRetryFromParentManager: this.onRetryFromParentManager,
  };

  onRetryFromParentManager(k: string) {
    if (k === 'downloadFile') {
      this.downloadFile();
    }
    if (k === 'deleteFile') {
      this.deleteFile();
    }
    if (k === 'uploadFile') {
      this.uploadFile();
    }
  }

  format = format;

  private dragingOver: boolean = false;

  private selectedFile: File | null = null;

  private uploadStatus: UploadedFileUpdate<UploadedFile> | null = null;

  private errorMessage: { title: string; description: string[] } | null = null;

  private get fileName() {
    return this.uploaded?.name || this.selectedFile?.name || '';
  }

  get shouldHideActions() {
    return this.hideActions !== false;
  }

  get readableAccept() {
    return this.acceptString || this.accept.toUpperCase().split(',').join(', ');
  }

  get shouldHideFooter() {
    return this.hideFooter !== false;
  }

  get withTooltip() {
    return this.tooltip !== false;
  }

  get maxSizeFormatted() {
    return formatBytes(this.maxSize);
  }

  get isLoading() {
    return this.loading !== false;
  }

  get isUploading() {
    return this.requestManager.requestStates.uploadFile === 'pending';
  }

  get isDownloading() {
    return this.requestManager.requestStates.downloadFile === 'pending';
  }

  get isDeleting() {
    return this.requestManager.requestStates.deleteFile === 'pending';
  }

  get deleteAllowed() {
    return this.deleteNotAllowed === false && this.deleteRequest.length > 0;
  }

  get downloadAllowed() {
    return this.downloadNotAllowed === false && this.downloadRequest.length > 0;
  }

  get uploadAllowed() {
    return this.uploadNotAllowed === false && this.uploadRequest.length > 0;
  }

  get isFileSelected() {
    return this.selectedFile !== null;
  }

  get fileReadyToBeUploaded() {
    return this.autoUpload !== true && this.selectedFile;
  }

  get hideUploadedByInformation() {
    return !this.uploaded || !this.uploaded.createdByName || !this.uploaded.createdAt || this.hideUpdatedBy !== false;
  }

  private getFileType(name?: string) {
    const fileName = name || this.fileName;
    const nameSplitted = fileName.split('.');
    return nameSplitted.length > 1 ? nameSplitted[nameSplitted.length - 1] : 'FILE';
  }

  cancelUpload() {
    this.requestManager.cancel('uploadFile');
    this.selectedFile = null;
    this.uploadStatus = null;
    this.$emit('update:selected', null);
  }

  downloadFile() {
    if (this.uploaded) {
      this.requestManager
        .sameOrNew('downloadFile', this.downloadRequest(this.uploaded), this.uploaded.link)
        .subscribe();
    }
  }

  deleteFile() {
    if (this.uploaded) {
      const confirm = () => {
        this.requestManager
          .sameOrNew('deleteFile', this.deleteRequest(this.uploaded!), this.uploaded!.link)
          .subscribe(() => {
            setTimeout(() => {
              this.selectedFile = null;
              this.uploadStatus = null;
              this.$emit('update:uploaded', null);
            }, 0);
          });
      };

      const modal = this.$refs.deleteConfirmModal;
      modal.show();
      modal.$once('ok', confirm);
      modal.$once('hidden', () => {
        modal.$off('ok', confirm);
      });
    }
  }

  uploadFile(eventTimeout = 1000, clearTimeout = 1000) {
    if (this.selectedFile) {
      this.requestManager.currentOrNew('uploadFile', this.uploadRequest(this.selectedFile)).subscribe(
        (progress) => {
          this.uploadStatus = progress;
          if (progress.finished) {
            setTimeout(() => this.$emit('update:uploaded', progress.file), eventTimeout);
            setTimeout(() => {
              this.selectedFile = null;
              this.uploadStatus = null;
            }, clearTimeout);
          }
        },
        () => {
          setTimeout(() => {
            this.selectedFile = null;
            this.uploadStatus = null;
          }, clearTimeout);
        }
      );
    }
  }

  onFilesDropped(event: DragEvent) {
    this.dragingOver = false;
    if (event.dataTransfer && !this.isUploading) {
      const out: File[] = [];
      Array.from(event.dataTransfer.files).forEach((file) => out.push(file));
      this.onFileSelection(out);
    }
  }

  onFileSelection(files: File[]) {
    const file = files[0];
    if (file) {
      if (this.maxSize > 0 && file.size > this.maxSize) {
        this.errorMessage = {
          title: 'File size limit',
          description: ['The file you tried to upload is too large.', 'Please reduce the file size and try again.'],
        };
      } else if (this.accept && !checkMIMEType(file, this.accept)) {
        this.errorMessage = {
          title: 'Invalid file format',
          description: ['Please upload document with supported format'],
        };
      } else {
        this.selectedFile = file;
        this.$emit('update:selected', file);
        if (this.autoUpload === true) {
          this.uploadFile();
        }
      }
    }
  }

  changeFileName(event: any) {
    if (event?.srcElement?.value) {
      let newValue = event?.srcElement?.value as string;
      const type = this.getFileType();
      if (this.getFileType(newValue) !== type) newValue = newValue.concat('.' + type);
      this.uploaded!.name = newValue;
      this.$emit('update:uploading', this.uploaded);
    }
  }

  @Watch('selected', { immediate: true })
  onSelectedChange() {
    this.selectedFile = this.selected;
  }
}
